import { useEffect, useState } from "react";
import sortUp from "../../images/sortUp.svg";
import {
  FormControl,
  MenuItem,
  Select,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import "./RcaTable.css";
import loadingSvg from "../../images/bouncing-circles.svg";
import { Pagination } from "../../components/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { updateSummaryBackTestingPageNumber } from "../../store/rcaSlice";
import DownloadButton from "../../components/common/DownloadButton";
import { downloadExcelFile } from "../../api/forecastPageApi";
import { useOktaAuth } from "@okta/okta-react";
import { setForecastFilter } from "../../store/filterSlice";
const uniqueKey = require("unique-key");

type DataRow = {
  upc: string;
  account: string;
  region: string;
  rca: string;
  cluster: string;
  pfeAccuracy: number;
  pfeBias: number;
  forecast: number;
  error: number;
  featureImportance: number;
  dpAccuracy: number;
  dpForecastAccuracy: number;
  dpForecastBias: number;
  forecastValueAdd: number;
  rcaTotal: string;
};

const RcaSummaryTable = () => {
  type SortKey = keyof DataRow;
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [ascending, setAscending] = useState<boolean>(true);
  const [selectValue] = useState<string>("Select");
  const [tagFilter, setTagFilter] = useState("All");
  const navigate = useNavigate();
  const rcaSummaryPaginationSlice = useSelector(
    (state: RootState) => state.rcaSummaryPaginationSlice
  );
  const userData = useSelector((state: RootState) => state?.user);
  const filter = useSelector((state: RootState) => state?.forecastFilter);
  const { authState } = useOktaAuth();
  const rcaData = useSelector((state: RootState) => state?.rcaData);
  const dispatch = useDispatch();
  const data = useSelector(
    (state: RootState) => state?.rcaData?.secondTable?.data
  );

  const tagOptions = Array.from(
    new Set(data?.map((row: any) => row.tag).filter(Boolean))
  );

  const payload: any = {
    shortTermMidTermCode: filter?.term.toString(),
    snapshotDate:
      filter?.term.toString() === "ST"
        ? filter?.shortSnapshotDate
        : filter?.midSnapshotDate,
    brandName: filter?.brand,
    subBrandName: filter?.subBrand,
    productLineName: filter?.productLine,
    productGroupName: filter?.productGroup,
    materialGlobalTradeItemNumberDescription: filter?.planningItem,
    packageSizeName: filter?.size,
    productCategoryName: filter?.category,
    eachUPC: filter?.eachUPC,
    portfolioGroupName: filter?.mg4PortfolioGroup,
    packageName: filter?.count,
    packTypeName: filter.ppg,
    keyAccount: filter?.keyAccount,
    customerGlobalName: filter?.global,
    customerSectorNumber: filter?.customerSector,
    customerRegionName: filter?.customerRegion,
    customerGroupName: filter?.customerGroup,
    customerBusinessUnitName: filter?.businessUnit,
    customerMarketUnitName: filter?.marketUnit,
    ibpDemandGroup: filter?.ibpDemandGroup,
    customerTradeSegmentDescription: filter?.cg1TrageSegment,
    salesSectorName: filter?.salesSector,
    salesRegionName: filter?.salesRegion,
    salesZoneName: filter?.zone,
    salesCountryCode: filter?.salesCountry,
    salesOrganizationCode: filter?.businessOrg,
    salesDivisionName: filter?.division,
    forecastEngineClusterId: filter?.clusters,
    forecastModuleCode: filter?.ac1ac2,
    forecastEngineSegmentationId: filter?.segment,
    packageTypeName: filter?.container,
    sector: userData?.currentSector?.sector,
    market: userData?.currentSector?.market,
    location: filter?.location,
    // LATAM Key
    flavorName: filter?.flavourNames,
    customerChannelName: filter?.channels,
    customerDemandGroup: filter?.demandGroups,
    customerSubChannelName: filter?.subChannels,
    customerRegionalCluster: filter?.regionalClusters,
    locationGlobalName: filter?.globalCode,
    locationSectorName: filter?.locationSector,
    locationCountryName: filter?.locationCountry,
    planningLocationId: filter?.planningLocationId,
    locationRegionName: filter?.regionCode,
    // PFNA key
    alternateIBPDemandGroup: filter?.alternateIBPDemandGroup,
  };

  const sortData = (key: SortKey, ascending: boolean) => {
    return [...data].sort((a: any, b: any) => {
      const aValue = a[key];
      const bValue = b[key];
      const ascReturn = calCulateAsceding(ascending, aValue, bValue);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return ascending ? aValue - bValue : bValue - aValue;
      }
      return ascReturn;
    });
  };

  const handleClick = () => {
    navigate("/statisticalforecastanalysis?invoke=true");
  };

  const calCulateAsceding = (
    ascending: boolean,
    aValue: string | number,
    bValue: string | number
  ) => {
    let result;

    if (ascending) {
      result = aValue > bValue ? 1 : -1;
    } else {
      result = aValue < bValue ? 1 : -1;
    }

    return result;
  };

  const theme = createTheme({
    palette: {
      background: {
        paper: "#272727",
      },
      text: {
        primary: "#ffff",
      },
    },
  });

  const handleDownload = () => {
    downloadExcelFile("RcaSummary", authState, {
      ...payload,
      pageNumber: 1,
      pageSize: 1000,
    });
  };

  const handleSort = (key: SortKey) => {
    setAscending(sortKey === key ? !ascending : true);
    setSortKey(key);
  };

  const sortedData = sortKey ? sortData(sortKey, ascending) : data;

  const getColor = (value: number) => {
    if (value <= 70) return "bg-red-700 text-white";
    if (value >= 78) return "bg-green-bg text-white";
    return "bg-yellow-bg text-white";
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortKey === key) {
      return ascending ? (
        <img src={sortUp} alt="sortUp" className="ml-2 w-3 h-3" />
      ) : (
        <img src={sortUp} alt="sortDown" className="ml-2 w-3 h-3" />
      );
    } else {
      return <img src={sortUp} alt="sortDown" className="ml-2 w-3 h-3" />;
    }
  };

  const handlePageNumberUpdateEventSummary = (currentPageNumber: number) => {
    dispatch(
      updateSummaryBackTestingPageNumber({
        pageLimit: rcaSummaryPaginationSlice?.pageLimit,
        pageNumber: currentPageNumber,
        totalPages: rcaSummaryPaginationSlice?.totalPages,
        isDataShownClicked: false,
        groupBySelectedField: [],
      })
    );
  };

  let tableContent;

  if (rcaData?.isTableDataLoading) {
    tableContent = (
      <div className="flex justify-center">
        <img
          src={loadingSvg}
          alt="loading"
          className="mt-10"
          width={40}
          height={40}
        />
      </div>
    );
  } else if (data?.length > 0) {
    tableContent = (
      <div className="RCA-table-holder">
        <table className="text-xs">
          <thead>
            <tr className="bg-ternary-bg">
              <th className="px-4 py-4 border border-primary-border">
                CLUSTER
              </th>
              <th className="px-4 py-4 border border-primary-border">
                {userData?.currentSector?.sector === "PFNA"
                  ? "Each UPC"
                  : "UPC"}
              </th>
              {userData?.currentSector?.sector !== "PFNA" && (
                <th className="px-4 py-4 border border-primary-border">
                  Description
                </th>
              )}
              <th className="px-4 py-4 border border-primary-border">
                KEY ACCOUNT
              </th>
              <th className="px-4 py-4 border border-primary-border">
                KEY ACCOUNT NAME
              </th>
              <th className="px-4 py-4 border border-primary-border">
                SALES REGION
              </th>
              <th className="px-4 py-4 border border-primary-border">
                SALES REGION NAME
              </th>
              {userData?.currentSector?.sector === "PFNA" && (
                <th className="px-4 py-4 border border-primary-border">
                  SALES ZONE
                </th>
              )}
              <th className="px-4 py-4 border border-primary-border">
                LOCATION
              </th>
              <th className="px-4 py-4 border border-primary-border">MODEL</th>

              <th className="px-4 py-4 border border-primary-border">
                RCA STAT FINAL
              </th>
              <th className="px-4 py-4 border border-primary-border">
                RCA TAG
              </th>
              <th
                className="px-4 py-4 border border-primary-border cursor-pointer"
                onClick={() => handleSort("pfeAccuracy")}
              >
                <div className="flex items-center justify-between">
                  (PFE) FORECAST ACCURACY STAT
                  {renderSortIcon("pfeAccuracy")}
                </div>
              </th>
              <th
                className="px-4 py-4 border border-primary-border cursor-pointer"
                onClick={() => handleSort("pfeBias")}
              >
                <div className="flex items-center justify-between">
                  (PFE) BIAS
                  {renderSortIcon("pfeBias")}
                </div>
              </th>
              <th
                className="px-4 py-4 border border-primary-border cursor-pointer"
                onClick={() => handleSort("forecast")}
              >
                <div className="flex items-center justify-between">
                  STATISTICAL FORECAST
                  {renderSortIcon("forecast")}
                </div>
              </th>
              <th
                className="px-4 py-4 border border-primary-border cursor-pointer"
                onClick={() => handleSort("error")}
              >
                <div className="flex items-center justify-between">
                  ABSOLUTE ERROR
                  {renderSortIcon("error")}
                </div>
              </th>
              <th
                className="px-4 py-4 border border-primary-border cursor-pointer"
                onClick={() => handleSort("dpForecastAccuracy")}
              >
                <div className="flex items-center justify-between">
                  (DP) FORECAST ACCURACY STAT
                  {renderSortIcon("dpForecastAccuracy")}
                </div>
              </th>
              <th
                className="px-4 py-4 border border-primary-border cursor-pointer"
                onClick={() => handleSort("dpForecastBias")}
              >
                <div className="flex items-center justify-between">
                  (DP) BIAS
                  {renderSortIcon("dpForecastBias")}
                </div>
              </th>
              <th
                className="px-4 py-4 border border-primary-border cursor-pointer"
                onClick={() => handleSort("forecastValueAdd")}
              >
                <div className="flex items-center justify-between">
                  (DP) FORECAST VALUE ADD
                  {renderSortIcon("forecastValueAdd")}
                </div>
              </th>
              <th className="px-4 py-4 border border-primary-border">
                RCA TOTAL
              </th>
              <th className="px-4 py-4 border border-primary-border">
                FEATURE & IMPORTANCE
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((row: any, idx: any) => (
              <tr
                key={uniqueKey("rca_summary_key")}
                className="border border-gray-200"
              >
                <td className="px-4 py-4 border border-primary-border">
                  {row.forecastEngineClusterId}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {userData?.currentSector?.sector === "PFNA"
                    ? row.businessReportingMaterialId
                    : row.materialGlobalTradeItemNumber}
                </td>
                {userData?.currentSector?.sector !== "PFNA" && (
                  <td className="px-4 py-4 border border-primary-border">
                    {row.materialGlobalTradeItemNumberDescription}
                  </td>
                )}
                <td className="px-4 py-4 border border-primary-border">
                  {userData?.currentSector?.sector === "PFNA"
                    ? row.businessReportingCustomerId
                    : row.demandPlanningKeyAccountId}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {userData?.currentSector?.sector === "PFNA"
                    ? row.businessReportingCustomerName
                    : row.demandPlanningKeyAccountName}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {row.salesRegionCode}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {row.salesRegionName}
                </td>
                {userData?.currentSector?.sector === "PFNA" && (
                  <td className="px-4 py-4 border border-primary-border">
                    {row.businessReportingGeographyName}
                  </td>
                )}
                <td className="px-4 py-4 border border-primary-border">
                  {row.locationName}-{row.locationId}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {row.model}
                </td>

                <td className="px-4 py-4 bordFer border-primary-border">
                  {row.finalRcaName}
                </td>
                <td
                  className={`px-2 py-2 border border-primary-border ${
                    !row.tag ? "text-gray-400" : "text-white"
                  }`}
                >
                  <span
                    className={`${
                      row.tag ? "bg-white rounded px-1 text-black" : ""
                    }`}
                  >
                    {row.tag || "—"}
                  </span>
                </td>

                <td
                  className={`px-4 py-4 border border-primary-border ${getColor(
                    Math.trunc(row.pfeAccuracy)
                  )}`}
                >
                  {`${Math.trunc(row.pfeAccuracy)}%`}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {`${Math.trunc(row.pfeBias)}%`}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {Math.trunc(row.statisticalForecast)}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {Math.trunc(row.absoluteErrorQuantity)}
                </td>
                <td
                  className={`px-4 py-4 border border-primary-border ${getColor(
                    row.dpForecastAccuracy
                  )}`}
                >
                  {Math.trunc(row.dpForecastAccuracy)}%
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {Math.trunc(row.dpForecastBias)}%
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {Math.trunc(row.forecastValueAdd)}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {row.rcaTotal || "-"}
                </td>
                <td className="px-4 py-4 border border-primary-border">
                  {row.featureImportance}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else {
    tableContent = (
      <div className="bg-ternary-bg rounded p-2">
        <div className="flex justify-center">
          <p className="p-2 m-1">
            No Data to show or no options selected, please choose from the above
            filters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="bg-ternary-bg text-white p-6 pb-10 rounded-lg shadow-lg">
        <div className="pb-4 flex justify-between">
          <div className="space-x-2">
            <ThemeProvider theme={theme}>
              <FormControl
                fullWidth
                sx={{
                  minWidth: "195px",
                  backgroundColor: "#313131",
                  borderRadius: "100px",
                }}
              >
                <Select
                  value={tagFilter}
                  onChange={(event) => {
                    const selectedTag: any = event.target.value;
                    setTagFilter(selectedTag);
                    dispatch(
                      setForecastFilter({ key: "rcaTag", value: selectedTag })
                    );
                  }}
                  placeholder="Select Tag"
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <MenuItem value="All">All Tags</MenuItem>
                  {rcaData?.rcaTags?.map((tag, index) => (
                    <MenuItem key={index} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ThemeProvider>
          </div>
          <div>
            <DownloadButton onClick={handleDownload} />
          </div>
        </div>

        {tableContent}

        {data?.length > 0 ? (
          <div className="mt-8 flex justify-between">
            <div className="flex-grow flex justify-between ">
              <div></div>
              <Pagination
                totalPages={rcaSummaryPaginationSlice?.totalPages}
                activePageNumber={rcaSummaryPaginationSlice?.pageNumber}
                updateBackTestingPageNumber={(data) =>
                  handlePageNumberUpdateEventSummary(data)
                }
              />
              <button
                className="write-back-button"
                onClick={handleClick}
                // disabled={!(extractTableData?.length > 0)}
              >
                View Forecast Analysis
              </button>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default RcaSummaryTable;
